/**
 * PWA Service Worker update manager.
 * 
 * - Registers the SW with `prompt` strategy (no auto-reload).
 * - Checks for updates every 15 minutes.
 * - When a new SW is waiting, fires a custom event so the UI can show a toast.
 * - After 24 h without user action, forces the update automatically.
 */

const CHECK_INTERVAL = 15 * 60 * 1000; // 15 min
const AUTO_UPDATE_DELAY = 24 * 60 * 60 * 1000; // 24 h

let registration: ServiceWorkerRegistration | null = null;
let autoUpdateTimer: ReturnType<typeof setTimeout> | null = null;

/** Apply the waiting SW and reload */
export function applyUpdate() {
  const waiting = registration?.waiting;
  if (waiting) {
    console.log("[PWA] Sending SKIP_WAITING to waiting SW");
    waiting.postMessage({ type: "SKIP_WAITING" });
  } else {
    // Fallback: just reload
    console.log("[PWA] No waiting SW found, reloading");
    window.location.reload();
  }
}

/** Dispatch a custom event the UI listens to */
function notifyUpdateAvailable() {
  console.log("[PWA] Update available — showing toast");
  window.dispatchEvent(new CustomEvent("pwa-update-available"));

  // Auto-update after 24 h if user ignores
  if (autoUpdateTimer) clearTimeout(autoUpdateTimer);
  autoUpdateTimer = setTimeout(() => {
    console.log("[PWA] Auto-updating after 24 h timeout");
    applyUpdate();
  }, AUTO_UPDATE_DELAY);
}

function onNewSW(sw: ServiceWorker) {
  if (sw.state === "installed") {
    // Only notify if there's already a controller (= this is an UPDATE, not first install)
    if (navigator.serviceWorker.controller) {
      notifyUpdateAvailable();
    } else {
      console.log("[PWA] First install — skipping update toast");
    }
  } else {
    sw.addEventListener("statechange", () => {
      if (sw.state === "installed" && navigator.serviceWorker.controller) {
        notifyUpdateAvailable();
      }
    });
  }
}

export async function registerSW() {
  if (!("serviceWorker" in navigator)) return;

  // Reload when a new SW takes control
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    console.log("[PWA] Controller changed — reloading");
    window.location.reload();
  });

  try {
    registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    console.log("[PWA] SW registered, scope:", registration.scope);

    // Already a waiting worker (e.g. from a previous visit)
    if (registration.waiting) {
      console.log("[PWA] Found waiting SW on load");
      notifyUpdateAvailable();
    }

    // New SW installing right now
    if (registration.installing) {
      console.log("[PWA] SW is installing");
      onNewSW(registration.installing);
    }

    // Future updates
    registration.addEventListener("updatefound", () => {
      const newSW = registration!.installing;
      console.log("[PWA] Update found, new SW installing");
      if (newSW) onNewSW(newSW);
    });

    // Periodic update checks
    setInterval(() => {
      console.log("[PWA] Checking for updates…");
      registration?.update().catch(() => {});
    }, CHECK_INTERVAL);
  } catch (err) {
    console.warn("[PWA] SW registration failed:", err);
  }
}
